import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { getJobs, type Job } from '@/lib/api/jobs';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

// ── Equipment (CR #100, completed per CR #120) ──────────────────────────────
// Inventory with per-tool condition / service history / photo / receipt,
// category → subcategory structure, a shopping list (manual + AI gap-finder),
// and a built-in catalog of common gear to add from.

interface EquipmentItem {
  id: number;
  brand: string;
  model: string | null;
  name: string;
  category: string | null;
  subcategory: string | null;
  serial: string | null;
  qty: number;
  replacement_value: number;
  status: 'available' | 'in_use' | 'repair' | 'lost' | 'sold';
  condition_grade: 'new' | 'good' | 'fair' | 'poor';
  last_service: string | null;
  service_cost: number | null;
  photo_path: string | null;
  receipt_path: string | null;
  assigned_job_id: number | null;
  job_title: string | null;
  notes: string | null;
}

interface ShoppingItem {
  id: number; name: string; brand: string | null; category: string | null;
  est_price: number | null; qty: number; priority: 'low' | 'normal' | 'high';
  source: 'manual' | 'ai' | 'catalog'; notes: string | null; purchased: number;
}

interface CatalogItem { name: string; brand: string; category: string; subcategory: string; est_price: number }
interface Suggestion { name: string; brand: string | null; category: string; est_price: number; priority: string; reason: string }
interface Summary { count: number; line_items: number; total_value: number; in_use: number; in_repair: number }

const money = (n: number) => `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const statusChip: Record<EquipmentItem['status'], { label: string; cls: string }> = {
  available: { label: 'Available', cls: 'bg-success/10 text-success' },
  in_use: { label: 'In use', cls: 'bg-primary/10 text-primary' },
  repair: { label: 'In repair', cls: 'bg-warning/10 text-warning' },
  lost: { label: 'Lost', cls: 'bg-critical/10 text-critical' },
  sold: { label: 'Sold', cls: 'bg-gray-100 text-gray-500' },
};

const conditionChip: Record<EquipmentItem['condition_grade'], string> = {
  new: 'bg-green-100 text-green-700',
  good: 'bg-sky-100 text-sky-700',
  fair: 'bg-amber-100 text-amber-700',
  poor: 'bg-red-100 text-red-700',
};

const EMPTY_FORM = {
  brand: '', model: '', name: '', category: '', subcategory: '', serial: '', qty: '1',
  replacement_value: '', notes: '', condition_grade: 'good', last_service: '', service_cost: '',
};

type Tab = 'inventory' | 'shopping' | 'catalog';

export function EquipmentPage() {
  const { toast, toasts, removeToast } = useToast();
  const [tab, setTab] = useState<Tab>('inventory');
  const [items, setItems] = useState<EquipmentItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [categories, setCategories] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmDelete, setConfirmDelete] = useState<EquipmentItem | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const receiptRef = useRef<HTMLInputElement>(null);

  // Shopping + catalog
  const [shopping, setShopping] = useState<ShoppingItem[]>([]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [shopForm, setShopForm] = useState({ name: '', brand: '', category: '', est_price: '', qty: '1', priority: 'normal' });

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get('/equipment');
      setItems(r.data.data.items);
      setSummary(r.data.data.summary);
    } catch { toast.error('Failed to load equipment'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadShopping = useCallback(() => {
    apiClient.get('/equipment/shopping/list').then(r => setShopping(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadShopping(); }, [load, loadShopping]);
  useEffect(() => {
    getJobs({ limit: 200 }).then(r => setJobs(r.jobs.filter(j => !['completed', 'cancelled'].includes(j.status || '')))).catch(() => {});
    apiClient.get('/equipment/meta').then(r => setCategories(r.data.data.categories)).catch(() => {});
    apiClient.get('/equipment/catalog/list').then(r => setCatalog(r.data.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = items;
    if (catFilter) list = list.filter(i => i.category === catFilter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(i =>
      [i.brand, i.model, i.name, i.category, i.subcategory, i.serial, i.job_title].filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q)));
    return list;
  }, [items, search, catFilter]);

  const byCategory = useMemo(() => {
    const map = new Map<string, EquipmentItem[]>();
    for (const i of filtered) {
      const key = i.category || 'Uncategorized';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(i);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const startAdd = () => { setEditingId(null); setForm(EMPTY_FORM); setShowAdd(true); };
  const startEdit = (i: EquipmentItem) => {
    setEditingId(i.id);
    setForm({
      brand: i.brand, model: i.model || '', name: i.name, category: i.category || '',
      subcategory: i.subcategory || '', serial: i.serial || '', qty: String(i.qty),
      replacement_value: String(i.replacement_value), notes: i.notes || '',
      condition_grade: i.condition_grade || 'good',
      last_service: i.last_service ? String(i.last_service).slice(0, 10) : '',
      service_cost: i.service_cost != null ? String(i.service_cost) : '',
    });
    setShowAdd(true);
  };

  const saveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.brand.trim() || !form.name.trim()) { toast.error('Brand and name are required'); return; }
    const payload = {
      brand: form.brand, model: form.model || null, name: form.name,
      category: form.category || null, subcategory: form.subcategory || null,
      serial: form.serial || null, qty: parseInt(form.qty, 10) || 1,
      replacement_value: parseFloat(form.replacement_value) || 0, notes: form.notes || null,
      condition_grade: form.condition_grade,
      last_service: form.last_service || null,
      service_cost: form.service_cost ? parseFloat(form.service_cost) : null,
    };
    try {
      if (editingId) { await apiClient.put(`/equipment/${editingId}`, payload); toast.success('Updated'); }
      else { await apiClient.post('/equipment', payload); toast.success('Added to inventory'); }
      setShowAdd(false);
      load();
    } catch { toast.error('Failed to save'); }
  };

  const uploadFile = async (kind: 'photo' | 'receipt', file: File) => {
    if (!editingId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await apiClient.post(`/equipment/${editingId}/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(kind === 'photo' ? 'Photo attached' : 'Receipt attached');
      load();
    } catch { toast.error('Upload failed'); }
  };

  const assign = async (i: EquipmentItem, jobId: string) => {
    setBusyId(i.id);
    try { await apiClient.put(`/equipment/${i.id}`, { assigned_job_id: jobId ? Number(jobId) : null }); load(); }
    catch { toast.error('Failed to update'); }
    finally { setBusyId(null); }
  };

  const setStatus = async (i: EquipmentItem, status: string) => {
    setBusyId(i.id);
    try { await apiClient.put(`/equipment/${i.id}`, { status, ...(status !== 'in_use' ? { assigned_job_id: null } : {}) }); load(); }
    catch { toast.error('Failed to update'); }
    finally { setBusyId(null); }
  };

  const doDelete = async (i: EquipmentItem) => {
    setConfirmDelete(null);
    try {
      await apiClient.delete(`/equipment/${i.id}`);
      setItems(prev => prev.filter(x => x.id !== i.id));
      toast.success('Removed');
    } catch { toast.error('Failed to delete'); }
  };

  // ── Shopping list actions ──
  const addShopping = async (row: { name: string; brand?: string | null; category?: string | null; est_price?: number | string | null; qty?: number | string; priority?: string; source: string; notes?: string | null }) => {
    try {
      await apiClient.post('/equipment/shopping', row);
      loadShopping();
      toast.success(`Added "${row.name}" to the shopping list`);
    } catch { toast.error('Failed to add'); }
  };

  const suggest = async () => {
    setSuggesting(true);
    setSuggestions(null);
    try {
      const r = await apiClient.post('/equipment/shopping/suggest');
      setSuggestions(r.data.data);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } } };
      toast.error(ax.response?.data?.error || 'AI suggestion failed');
    } finally { setSuggesting(false); }
  };

  const shoppingTotal = shopping.filter(s => !s.purchased).reduce((s, r) => s + (Number(r.est_price) || 0) * r.qty, 0);
  const subcatsFor = (cat: string) => categories[cat] || [];

  const catalogByCategory = useMemo(() => {
    const map = new Map<string, CatalogItem[]>();
    for (const c of catalog) { if (!map.has(c.category)) map.set(c.category, []); map.get(c.category)!.push(c); }
    return [...map.entries()];
  }, [catalog]);

  return (
    <MainLayout>
      {/* Single wrapper: main is a height-constrained flex column, so multiple
          direct children get flex-shrunk and overflow-hidden cards clip. */}
      <div className="space-y-5">
      <LargeTitleHeader
        title="Equipment"
        subtitle="Inventory, condition & service records, and what to buy next"
        actions={tab === 'inventory' ? (
          <button onClick={startAdd}
            className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90">
            <span className="material-symbols-outlined text-base">add</span>
            <span className="hidden sm:inline">Add equipment</span>
          </button>
        ) : undefined}
      />

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {([['inventory', 'Inventory'], ['shopping', `Shopping list${shopping.filter(s => !s.purchased).length ? ` (${shopping.filter(s => !s.purchased).length})` : ''}`], ['catalog', 'Catalog']] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3.5 py-1.5 text-sm font-semibold rounded-md transition-colors ${tab === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'inventory' && (
        <>
          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Replacement value</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{money(summary.total_value)}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Units</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{summary.count}</p>
                <p className="text-[11px] text-gray-400">{summary.line_items} line items</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">Out on jobs</p>
                <p className="text-xl font-bold text-primary mt-1">{summary.in_use}</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">In repair</p>
                <p className={`text-xl font-bold mt-1 ${summary.in_repair > 0 ? 'text-warning' : 'text-gray-900'}`}>{summary.in_repair}</p>
              </div>
            </div>
          )}

          {/* Category chips + search */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setCatFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!catFilter ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>All</button>
            {Object.keys(categories).map(c => (
              <button key={c} onClick={() => setCatFilter(catFilter === c ? '' : c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${catFilter === c ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>
            ))}
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search brand, tool, serial…"
              className="ml-auto w-56 h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>

          {showAdd && (
            <form onSubmit={saveForm} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} placeholder="Brand *" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <input value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="Model #" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <input value={form.serial} onChange={e => setForm(f => ({ ...f, serial: e.target.value }))} placeholder="Serial #" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Description *" className="h-9 px-2 rounded-lg border border-gray-300 text-sm sm:col-span-3" />
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value, subcategory: '' }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
                <option value="">Category…</option>
                {Object.keys(categories).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.subcategory} onChange={e => setForm(f => ({ ...f, subcategory: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white" disabled={!form.category}>
                <option value="">Subcategory…</option>
                {subcatsFor(form.category).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={form.condition_grade} onChange={e => setForm(f => ({ ...f, condition_grade: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
                {['new', 'good', 'fair', 'poor'].map(c => <option key={c} value={c}>Condition: {c}</option>)}
              </select>
              <input type="number" min="1" value={form.qty} onChange={e => setForm(f => ({ ...f, qty: e.target.value }))} placeholder="Qty" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <input type="number" step="0.01" min="0" value={form.replacement_value} onChange={e => setForm(f => ({ ...f, replacement_value: e.target.value }))} placeholder="Replacement $ (total)" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">Last service
                <input type="date" value={form.last_service} onChange={e => setForm(f => ({ ...f, last_service: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm text-gray-800" />
              </label>
              <label className="flex flex-col gap-0.5 text-xs text-gray-500">Service cost $
                <input type="number" step="0.01" min="0" value={form.service_cost} onChange={e => setForm(f => ({ ...f, service_cost: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm text-gray-800" />
              </label>
              {editingId ? (
                <div className="flex items-end gap-2">
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadFile('photo', e.target.files[0])} />
                  <input ref={receiptRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => e.target.files?.[0] && uploadFile('receipt', e.target.files[0])} />
                  <button type="button" onClick={() => photoRef.current?.click()} className="h-9 px-3 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">photo_camera</span> Photo
                  </button>
                  <button type="button" onClick={() => receiptRef.current?.click()} className="h-9 px-3 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 flex items-center gap-1">
                    <span className="material-symbols-outlined text-base">receipt</span> Service receipt
                  </button>
                </div>
              ) : <div className="hidden sm:block" />}
              <div className="flex gap-2 sm:col-span-3 justify-end">
                <button type="button" onClick={() => setShowAdd(false)} className="h-9 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
                <button type="submit" className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90">{editingId ? 'Save' : 'Add'}</button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <span className="material-symbols-outlined animate-spin text-3xl text-gray-300">progress_activity</span>
            </div>
          ) : byCategory.length === 0 ? (
            <p className="text-sm text-gray-400 py-10 text-center">No equipment matches.</p>
          ) : byCategory.map(([cat, list]) => (
            <div key={cat} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50/60 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">{cat}</h3>
                <span className="text-xs text-gray-500">{list.length} item{list.length !== 1 ? 's' : ''} · {money(list.reduce((s, i) => s + i.replacement_value, 0))}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                      <th className="px-5 py-2 font-semibold">Item</th>
                      <th className="px-2 py-2 font-semibold">Condition</th>
                      <th className="px-2 py-2 font-semibold">Serviced</th>
                      <th className="px-2 py-2 font-semibold text-right">Value</th>
                      <th className="px-2 py-2 font-semibold">Status</th>
                      <th className="px-2 py-2 font-semibold">Where</th>
                      <th className="px-5 py-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {list.map(i => (
                      <tr key={i.id} className={`hover:bg-gray-50 ${busyId === i.id ? 'opacity-50' : ''}`}>
                        <td className="px-5 py-2.5">
                          <div className="flex items-center gap-2.5">
                            {i.photo_path ? (
                              <button type="button" onClick={() => setLightbox(`/${i.photo_path}`)} className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                                <img src={`/${i.photo_path}`} alt="" className="w-full h-full object-cover" />
                              </button>
                            ) : (
                              <span className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 flex-shrink-0">
                                <span className="material-symbols-outlined text-lg">handyman</span>
                              </span>
                            )}
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900">{i.brand} {i.model ? `${i.model} — ` : ''}{i.name}{i.qty > 1 ? ` ×${i.qty}` : ''}</p>
                              <p className="text-[11px] text-gray-400 truncate">{[i.subcategory, i.serial && `SN ${i.serial}`, i.notes].filter(Boolean).join(' · ')}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2.5">
                          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 capitalize ${conditionChip[i.condition_grade] || 'bg-gray-100 text-gray-500'}`}>{i.condition_grade}</span>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {i.last_service ? String(i.last_service).slice(0, 10) : '—'}
                          {i.service_cost != null && <span className="text-gray-400"> · {money(Number(i.service_cost))}</span>}
                          {i.receipt_path && (
                            <a href={`/${i.receipt_path}`} target="_blank" rel="noopener noreferrer" title="Service receipt" className="ml-1 text-primary align-middle">
                              <span className="material-symbols-outlined text-sm">receipt</span>
                            </a>
                          )}
                        </td>
                        <td className="px-2 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{money(i.replacement_value)}</td>
                        <td className="px-2 py-2.5">
                          <select value={i.status} onChange={e => setStatus(i, e.target.value)}
                            className={`text-[11px] font-semibold rounded-full px-1.5 py-1 border-0 cursor-pointer ${statusChip[i.status].cls}`}>
                            {(Object.keys(statusChip) as EquipmentItem['status'][]).map(s => (
                              <option key={s} value={s}>{statusChip[s].label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-2.5">
                          <select value={i.assigned_job_id ?? ''} onChange={e => assign(i, e.target.value)}
                            className="max-w-[11rem] text-xs border border-gray-200 rounded-lg px-1.5 py-1 bg-white text-gray-600">
                            <option value="">— shop</option>
                            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-2.5">
                          <div className="flex items-center justify-end gap-0.5">
                            <button onClick={() => startEdit(i)} title="Edit (photos & service live here)"
                              className="h-8 w-8 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 flex items-center justify-center">
                              <span className="material-symbols-outlined text-base">edit</span>
                            </button>
                            <button onClick={() => setConfirmDelete(i)} title="Delete"
                              className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                              <span className="material-symbols-outlined text-base">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {tab === 'shopping' && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-gray-500">
              {shopping.filter(s => !s.purchased).length} to buy · est. <strong className="text-gray-800">{money(shoppingTotal)}</strong>
            </p>
            <button onClick={suggest} disabled={suggesting}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[#2C1810] text-white text-sm font-bold disabled:opacity-60">
              <span className={`material-symbols-outlined text-base ${suggesting ? 'animate-spin' : ''}`}>{suggesting ? 'progress_activity' : 'auto_awesome'}</span>
              {suggesting ? 'Thinking…' : "What am I missing? (AI)"}
            </button>
          </div>

          {/* AI suggestions */}
          {suggestions && (
            <div className="bg-white rounded-xl border border-[#2C1810]/20 p-4 space-y-2">
              <p className="text-sm font-bold text-gray-900">Suggested gaps for a framing/GC outfit</p>
              {suggestions.length === 0 && <p className="text-sm text-gray-400">Nothing obvious missing — well equipped.</p>}
              {suggestions.map((s, i) => (
                <div key={i} className="flex items-center gap-3 border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{s.name}{s.brand ? ` — ${s.brand}` : ''}
                      <span className="ml-2 text-xs text-gray-400">{s.category} · ~{money(Number(s.est_price) || 0)}</span>
                    </p>
                    <p className="text-xs text-gray-500">{s.reason}</p>
                  </div>
                  <button onClick={() => { addShopping({ ...s, source: 'ai', notes: s.reason }); setSuggestions(cur => cur?.filter((_, j) => j !== i) ?? null); }}
                    className="h-8 px-3 rounded-lg bg-primary text-white text-xs font-bold flex-shrink-0">Add</button>
                </div>
              ))}
            </div>
          )}

          {/* Manual add */}
          <form onSubmit={e => { e.preventDefault(); if (shopForm.name.trim()) { addShopping({ ...shopForm, source: 'manual' }); setShopForm({ name: '', brand: '', category: '', est_price: '', qty: '1', priority: 'normal' }); } }}
            className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-2 sm:grid-cols-6 gap-2">
            <input value={shopForm.name} onChange={e => setShopForm(f => ({ ...f, name: e.target.value }))} placeholder="What do you need? *" className="h-9 px-2 rounded-lg border border-gray-300 text-sm col-span-2" />
            <input value={shopForm.brand} onChange={e => setShopForm(f => ({ ...f, brand: e.target.value }))} placeholder="Brand" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
            <input type="number" step="0.01" value={shopForm.est_price} onChange={e => setShopForm(f => ({ ...f, est_price: e.target.value }))} placeholder="Est. $" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
            <select value={shopForm.priority} onChange={e => setShopForm(f => ({ ...f, priority: e.target.value }))} className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
              <option value="high">High priority</option><option value="normal">Normal</option><option value="low">Low</option>
            </select>
            <button type="submit" className="h-9 rounded-lg bg-primary text-white text-sm font-semibold">Add</button>
          </form>

          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-50">
            {shopping.length === 0 && <p className="text-sm text-gray-400 py-8 text-center">Shopping list is empty — add items or ask the AI what you're missing.</p>}
            {shopping.map(s => (
              <div key={s.id} className={`flex items-center gap-3 px-4 py-2.5 ${s.purchased ? 'opacity-45' : ''}`}>
                <input type="checkbox" checked={!!s.purchased} className="accent-[#C77D3F]"
                  onChange={async () => { await apiClient.put(`/equipment/shopping/${s.id}`, { purchased: s.purchased ? 0 : 1 }); loadShopping(); }}
                  title={s.purchased ? 'Mark as still needed' : 'Mark purchased'} />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium text-gray-900 ${s.purchased ? 'line-through' : ''}`}>
                    {s.qty > 1 ? `${s.qty}× ` : ''}{s.name}{s.brand ? ` — ${s.brand}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {[s.category, s.est_price != null ? `~${money(s.est_price)}` : null, s.notes].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${s.priority === 'high' ? 'bg-red-100 text-red-700' : s.priority === 'low' ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-700'}`}>{s.priority}</span>
                {s.source !== 'manual' && <span className="text-[10px] text-gray-400 uppercase">{s.source}</span>}
                <button onClick={async () => { await apiClient.delete(`/equipment/shopping/${s.id}`); loadShopping(); }}
                  className="h-8 w-8 rounded-lg text-gray-300 hover:text-red-500 flex items-center justify-center">
                  <span className="material-symbols-outlined text-base">delete</span>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'catalog' && (
        <>
          <p className="text-sm text-gray-500">Common gear for an outfit like yours, with typical Canadian street prices — one tap to put it on the shopping list.</p>
          {catalogByCategory.map(([cat, list]) => (
            <div key={cat} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-2.5 border-b border-gray-100 bg-gray-50/60">
                <h3 className="text-sm font-bold text-gray-900">{cat}</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {list.map((c, i) => {
                  const owned = items.some(it => it.name.toLowerCase().includes(c.name.toLowerCase().slice(0, 12)));
                  return (
                    <div key={i} className="flex items-center gap-3 px-5 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{c.name} <span className="text-gray-400 font-normal">— {c.brand}</span></p>
                        <p className="text-xs text-gray-400">{c.subcategory} · ~{money(c.est_price)}</p>
                      </div>
                      {owned && <span className="text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-2 py-0.5">You may own this</span>}
                      <button onClick={() => addShopping({ name: c.name, brand: c.brand, category: c.category, est_price: c.est_price, source: 'catalog' })}
                        className="h-8 px-3 rounded-lg border border-primary/40 text-primary text-xs font-bold hover:bg-primary/5 flex-shrink-0">
                        Add to list
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Equipment" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Remove "${confirmDelete.name}" from the inventory?`}
          onConfirm={() => doDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      </div>
    </MainLayout>
  );
}
